import { $isCodeNode } from "@lexical/code-core";
import { $createHashtagNode, $isHashtagNode, HashtagNode } from "@lexical/hashtag";
import { $isLinkNode } from "@lexical/link";
import { registerLexicalTextEntity } from "@lexical/text";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $createTextNode,
  $getNearestNodeFromDOMNode,
  $hasUpdateTag,
  defineExtension,
  HISTORY_MERGE_TAG,
  safeCast,
  TextNode,
  type LexicalEditor,
  type LexicalExtension,
} from "lexical";
import { CONTENT_CLASSES } from "../../core/content-classes";
import { findHashtag, tagName } from "../../core/hashtags";

export interface HashtagsConfig {
  /** A ref, because the host's handler changes between renders and the editor outlives them. */
  activate: { current: ((tag: string) => void) | null };
}

/** Where text is literal: code, in a block or the inline format, and a link's label. */
function $isLiteral(node: TextNode): boolean {
  return (
    node.hasFormat("code") ||
    $findMatchingParent(node, (parent) => $isCodeNode(parent) || $isLinkNode(parent)) !== null
  );
}

function $toPlainText(node: HashtagNode): void {
  node.replace(
    $createTextNode(node.getTextContent())
      .setFormat(node.getFormat())
      .setStyle(node.getStyle())
      .setDetail(node.getDetail()),
  );
}

/**
 * The editor as `registerLexicalTextEntity` sees it: every transform it registers first checks two
 * things the matcher cannot, since it is given only text.
 *
 * Opening a document is not an edit. Lexical 0.51 runs transforms over a freshly parsed state, so
 * unguarded, a note with `#launch` typed before tags existed would change as it opened. The guard
 * cannot go in the matcher, as the link policy's does: upstream reads "no match" on a hashtag as
 * "no longer a tag", and would turn every stored tag back into text on the way in.
 *
 * Literal text holds no tags. A tag that ends up in code or a link becomes text again, and text
 * there is never promoted, which is what keeps the two from undoing each other.
 */
function guarded(editor: LexicalEditor): LexicalEditor {
  const facade: LexicalEditor = Object.create(editor);
  facade.registerNodeTransform = (klass, transform) =>
    editor.registerNodeTransform(klass, (node) => {
      if ($hasUpdateTag(HISTORY_MERGE_TAG)) return;
      if (node instanceof TextNode && $isLiteral(node)) {
        if ($isHashtagNode(node)) $toPlainText(node);
        return;
      }
      transform(node);
    });
  return facade;
}

/**
 * Turns `#tag` into a hashtag as it is typed, pasted, or imported from Markdown, and a click on one
 * into a call to the host with the tag's name. Registered in every preset: a preset never decides
 * what can be loaded.
 */
export const HashtagsExtension: LexicalExtension<
  HashtagsConfig,
  "z-editor/hashtags",
  unknown,
  unknown
> = defineExtension({
  name: "z-editor/hashtags",
  nodes: [HashtagNode],
  config: safeCast<HashtagsConfig>({ activate: { current: null } }),
  register(editor, config) {
    const onClick = (event: MouseEvent): void => {
      const activate = config.activate.current;
      const target =
        event.target instanceof Element
          ? event.target.closest(`.${CONTENT_CLASSES.hashtag}`)
          : null;
      if (activate === null || target === null) return;
      const text = editor.read(() => {
        const node = $getNearestNodeFromDOMNode(target);
        return $isHashtagNode(node) ? node.getTextContent() : null;
      });
      if (text !== null) activate(tagName(text));
    };
    return mergeRegister(
      ...registerLexicalTextEntity(guarded(editor), findHashtag, HashtagNode, (text) =>
        $createHashtagNode(text.getTextContent()),
      ),
      editor.registerRootListener((root, previous) => {
        previous?.removeEventListener("click", onClick);
        root?.addEventListener("click", onClick);
      }),
    );
  },
});
